import { NetworkStatus, QueryHookOptions, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import staticConfig from 'src/config'
import swarmClient from '../clients/swarm-client'
import { InvestAssetsQuery } from '../queries/swarm/InvestAssetsQuery'
import {
  AssetTokenFilter,
  InvestAssetsQuery as InvestAssetsGQL,
  InvestAssetsQueryVariables,
} from '../types/swarm/graphql'
import { normalizeInvestAssetTokens } from '../utils/invest'

const POLL_INTERVAL = 15000

const { isDevMode, isProdMode } = staticConfig

const staticFilter: AssetTokenFilter = {
  frozen: false,
  isOnSafeGuard: false,
  enabled: true,
  ...(isDevMode && {
    name_in: ['TESLA INC. DL - 001', 'iShares $ Treasury Bond 0-1yr UCITS ETF'], // This is the only assets which price feeds are available on testnet
  }),
  ...(isProdMode && {
    kyaData_: {
      // temp solution to filter out extra tokens
      assetType_contains: 'token',
    },
  }),
}

const useInvestAssets = (
  options?: QueryHookOptions<InvestAssetsGQL, InvestAssetsQueryVariables>
) => {
  const filter = { ...options?.variables?.filter }
  const isOrOperator = !!filter.or
  if (filter.or) {
    filter.or = filter.or?.map(expression => ({
      ...staticFilter,
      ...expression,
    }))
  }
  const { data, loading, error, fetchMore, networkStatus, refetch } = useQuery(
    InvestAssetsQuery,
    {
      fetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      pollInterval: POLL_INTERVAL,
      ...options,
      variables: {
        skip: 0,
        ...options?.variables,
        filter: {
          ...(!isOrOperator && staticFilter),
          ...filter,
        },
      },
      client: swarmClient,
    }
  )
  const initialLoading = loading && data?.assetTokens.length === undefined

  return useMemo(
    () => ({
      investAssets: normalizeInvestAssetTokens(data?.assetTokens || []),
      loading:
        initialLoading ||
        ![
          NetworkStatus.ready,
          NetworkStatus.error,
          NetworkStatus.poll,
        ].includes(networkStatus),
      error,
      refetch,
      refetching: [NetworkStatus.poll].includes(networkStatus),
      fetchMore: fetchMore,
    }),
    [data, error, fetchMore, initialLoading, networkStatus, refetch]
  )
}

export default useInvestAssets
